import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ArrowUpRight, Pencil, Trash2, Webhook } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { WebhookCreateDialog } from '@/components/WebhookCreateDialog';
import { WebhookEditDialog } from '@/components/WebhookEditDialog';
import { ApiError } from '@/lib/fetcher';
import {
  type Webhook as WebhookRow,
  deleteWebhook,
  fetchWebhooks,
  updateWebhook,
  webhookListKey,
} from '@/lib/queries';

export function WebhookList() {
  const q = useQuery({ queryKey: webhookListKey, queryFn: fetchWebhooks });
  const qc = useQueryClient();

  const [editing, setEditing] = React.useState<WebhookRow | null>(null);
  const [deleting, setDeleting] = React.useState<WebhookRow | null>(null);

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateWebhook(id, { active }),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: webhookListKey });
      const previous = qc.getQueryData<{ data: WebhookRow[]; total: number }>(
        webhookListKey,
      );
      if (previous) {
        qc.setQueryData(webhookListKey, {
          ...previous,
          data: previous.data.map((row) =>
            row.id === id ? { ...row, active } : row,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(webhookListKey, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: webhookListKey });
    },
  });

  const remove = useMutation({
    mutationFn: deleteWebhook,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: webhookListKey });
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Webhooks</h1>
          <p className="text-muted-foreground">
            Endpoints that receive event payloads. Up to one secret per
            webhook; we sign every delivery with HMAC-SHA256.
          </p>
        </div>
        <WebhookCreateDialog />
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Registered webhooks</CardTitle>
          <CardDescription>
            Toggle a row active to start receiving events. Deliveries
            page shows attempt history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load webhooks (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data && q.data.data.length === 0 ? (
            <EmptyState
              icon={Webhook}
              title="No webhooks yet"
              description="Click '+ New webhook' above to register your first endpoint."
            />
          ) : q.data ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.data.map((wh) => (
                  <TableRow key={wh.id}>
                    <TableCell className="max-w-[260px] truncate font-mono text-xs">
                      {wh.url}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {wh.events.slice(0, 3).map((evt) => (
                          <Badge key={evt} variant="secondary">
                            {evt}
                          </Badge>
                        ))}
                        {wh.events.length > 3 ? (
                          <Badge variant="outline">
                            +{wh.events.length - 3}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={wh.active}
                          onChange={(e) =>
                            toggle.mutate({
                              id: wh.id,
                              active: e.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-input"
                        />
                        {wh.active ? 'On' : 'Off'}
                      </label>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {format(parseISO(wh.createdAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/dashboard/webhooks/${wh.id}/deliveries`}>
                            Deliveries{' '}
                            <ArrowUpRight className="ml-1 h-3 w-3" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditing(wh)}
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(wh)}
                          aria-label="Delete"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>

      <WebhookEditDialog
        webhook={editing}
        open={Boolean(editing)}
        onOpenChange={(o) => !o && setEditing(null)}
      />

      <AlertDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this webhook?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.url} will stop receiving events. This can't be
              undone — you'll need to register a new webhook to resume
              deliveries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleting) remove.mutate(deleting.id);
              }}
              disabled={remove.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
